import type { RJSFSchema, UiSchema } from '@rjsf/utils';
import type { ReactElement } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import ErrorState from 'app/shared-components/common/ErrorState';

import { PageSectionEditor } from '../components/PageSectionEditor';
import { useGetSectionDetails } from './hooks/useGetSectionDetails';
import { useUpdateSection } from './hooks/useUpdateSection';

const EditSectionPage = (): ReactElement => {
  const { pageId = '', sectionId = '' } = useParams<{ pageId: string; sectionId: string }>();
  const [searchParams] = useSearchParams();
  const websiteId = searchParams.get('websiteId') ?? '';

  const { sectionDetails, isLoading, isError, error } = useGetSectionDetails({
    websiteId,
    pageId,
    sectionId,
  });

  const { mutateAsync: updateSectionAsync, isPending: isSaving } = useUpdateSection();

  if (!pageId || !sectionId || !websiteId) {
    return (
      <ErrorState
        title="Missing required parameters"
        subtitle="Cannot load section without valid identifiers"
      />
    );
  }

  if (isError) {
    return (
      <ErrorState
        title="Failed to load section details"
        subtitle={error instanceof Error ? error.message : 'Try again later'}
      />
    );
  }

  return (
    <PageSectionEditor
      title={`Edit ${sectionDetails?.sectionType.displayName ?? 'Section'}`}
      schema={(sectionDetails?.sectionType.jsonSchema ?? {}) as RJSFSchema}
      uiSchema={(sectionDetails?.sectionType.uiSchema ?? {}) as UiSchema}
      formData={sectionDetails?.data}
      isLoading={isLoading}
      previewSectionId={sectionId}
      previewSectionType={sectionDetails?.sectionType.id}
      isSaving={isSaving}
      onSubmit={(formData) => updateSectionAsync({ websiteId, pageId, sectionId, data: formData })}
    />
  );
};

export default EditSectionPage;
